class ZptProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const all = (s) => [...this.querySelectorAll(s)];
    const one = (s) => this.querySelector(s);

    this.slides = all('[data-zpt-gallery-slide]');
    this.thumbnails = all('[data-zpt-gallery-thumbnail]');
    this.openButtons = all('[data-zpt-gallery-open]');

    this.previousButton = one('[data-zpt-gallery-previous]');
    this.nextButton = one('[data-zpt-gallery-next]');

    this.dialog = one('[data-zpt-gallery-dialog]');
    this.dialogSlides = all('[data-zpt-gallery-dialog-slide]');
    this.dialogImages = all('.zpt-product-gallery__dialog-image');
    this.dialogCloseButton = one('[data-zpt-gallery-close]');
    this.dialogPreviousButton = one('[data-zpt-gallery-dialog-previous]');
    this.dialogNextButton = one('[data-zpt-gallery-dialog-next]');

    this.currentIndex = 0;
    this.dialogIndex = 0;

    this.dialogImages.forEach((image) => {
      image.draggable = false;
      image.setAttribute('draggable', 'false');
    });

    this.bindEvents();
    this.select(0, false);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  bindEvents() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const { signal } = this.abortController;

    const on = (el, type, fn, options = {}) =>
      el?.addEventListener(type, fn, {
        signal,
        ...options
      });

    this.thumbnails.forEach((el) =>
      on(el, 'click', () =>
        this.select(+el.dataset.index)
      )
    );

    this.openButtons.forEach((el) =>
      on(el, 'click', () =>
        this.openDialog(+el.dataset.index)
      )
    );

    on(
      this.previousButton,
      'click',
      () => this.previous()
    );

    on(
      this.nextButton,
      'click',
      () => this.next()
    );

    on(
      this.dialogCloseButton,
      'click',
      () => this.closeDialog()
    );

    on(
      this.dialogPreviousButton,
      'click',
      () => this.dialogPrevious()
    );

    on(
      this.dialogNextButton,
      'click',
      () => this.dialogNext()
    );

    on(this.dialog, 'click', (event) => {
      if (event.target === this.dialog) {
        this.closeDialog();
      }
    });

    const preventImageSaveGesture = (event) => {
      const image = event.composedPath().find((target) =>
        target instanceof HTMLImageElement &&
        target.matches('.zpt-product-gallery__dialog-image')
      );

      if (!image) return;

      event.preventDefault();
      event.stopPropagation();
    };

    on(this.dialog, 'contextmenu', preventImageSaveGesture, { capture: true });
    on(this.dialog, 'dragstart', preventImageSaveGesture, { capture: true });
    on(document, 'contextmenu', preventImageSaveGesture, { capture: true });
    on(document, 'dragstart', preventImageSaveGesture, { capture: true });

    on(this.dialog, 'keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.dialogPrevious();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.dialogNext();
      }
    });

    this.bindSwipe(this, false, signal);
    this.bindSwipe(this.dialog, true, signal);
  }

  bindSwipe(element, dialog, signal) {
    if (!element) return;

    let x = 0;
    let y = 0;

    element.addEventListener(
      'touchstart',
      (event) => {
        if (!dialog && this.dialog?.open) return;

        const touch = event.touches[0];

        x = touch.clientX;
        y = touch.clientY;
      },
      {
        signal,
        passive: true
      }
    );

    element.addEventListener(
      'touchend',
      (event) => {
        if (!dialog && this.dialog?.open) return;

        const touch = event.changedTouches[0];

        const dx = touch.clientX - x;
        const dy = touch.clientY - y;

        if (
          Math.abs(dx) < 50 ||
          Math.abs(dx) <= Math.abs(dy)
        ) {
          return;
        }

        if (dialog) {
          dx < 0
            ? this.dialogNext()
            : this.dialogPrevious();
        } else {
          dx < 0
            ? this.next()
            : this.previous();
        }
      },
      {
        signal,
        passive: true
      }
    );
  }

  select(index, scroll = true) {
    if (!this.slides.length) return;

    index = this.wrap(
      index,
      this.slides.length
    );

    this.pauseInactiveMedia(index);

    this.slides.forEach((slide, i) => {
      const active = i === index;

      slide.hidden = !active;

      slide.setAttribute(
        'aria-hidden',
        String(!active)
      );
    });

    this.setSelected(
      this.thumbnails,
      index
    );

    this.currentIndex = index;

    if (scroll) {
      this.scrollTo(
        this.thumbnails[index]
      );
    }
  }

  previous() {
    this.select(
      this.currentIndex - 1
    );
  }

  next() {
    this.select(
      this.currentIndex + 1
    );
  }

  openDialog(mediaIndex) {
    if (!this.dialogSlides.length) return;

    const index =
      this.dialogSlides.findIndex(
        (slide) =>
          +slide.dataset.mediaIndex ===
          mediaIndex
      );

    if (index < 0) return;

    this.dialogIndex = index;

    this.renderDialog();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }
  }

  closeDialog() {
    if (this.dialog?.open) {
      this.dialog.close();
    }
  }

  selectDialog(index, direction = 0) {
    if (!this.dialogSlides.length) return;

    this.dialogIndex = this.wrap(
      index,
      this.dialogSlides.length
    );

    this.renderDialog(direction);
  }

  dialogPrevious() {
    this.selectDialog(
      this.dialogIndex - 1,
      -1
    );
  }

  dialogNext() {
    this.selectDialog(
      this.dialogIndex + 1,
      1
    );
  }

  renderDialog(direction = 0) {
    this.dialogSlides.forEach(
      (slide, i) => {
        slide.hidden =
          i !== this.dialogIndex;

        slide.classList.remove(
          'zpt-is-entering',
          'zpt-from-next',
          'zpt-from-previous'
        );
      }
    );

    const active =
      this.dialogSlides[
        this.dialogIndex
      ];

    if (
      direction &&
      !matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      active.classList.add(
        direction > 0
          ? 'zpt-from-next'
          : 'zpt-from-previous'
      );

      void active.offsetWidth;

      active.classList.add(
        'zpt-is-entering'
      );
    }
  }

  setSelected(items, index) {
    items.forEach((item, i) =>
      item.setAttribute(
        'aria-selected',
        String(i === index)
      )
    );
  }

  scrollTo(element) {
    element?.scrollIntoView({
      behavior: matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
        ? 'auto'
        : 'smooth',

      block: 'nearest',
      inline: 'nearest'
    });
  }

  wrap(index, total) {
    return (
      ((index % total) + total) %
      total
    );
  }

  pauseInactiveMedia(activeIndex) {
    this.slides.forEach(
      (slide, i) => {
        if (i === activeIndex) return;

        slide
          .querySelectorAll('video')
          .forEach((video) =>
            video.pause()
          );
      }
    );
  }
}

if (
  !customElements.get(
    'zpt-product-gallery'
  )
) {
  customElements.define(
    'zpt-product-gallery',
    ZptProductGallery
  );
}
